import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { db } from '../firebase/config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  RadioGroup,
  FormControlLabel,
  Radio
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import CreditCardPayment from '../components/CreditCardPayment';

function Checkout() {
  const { currentUser } = useAuth();
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    address: '',
    city: '',
    phone: '',
    district: '',
    zipCode: ''
  });
  const [orderComplete, setOrderComplete] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('havale');
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);
  const [customerIP, setCustomerIP] = useState('1.2.3.4');

  // Toplam tutarı hesaplama fonksiyonu
  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login?redirect=/checkout');
      return;
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const getIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setCustomerIP(data.ip);
      } catch (error) {
        console.error('IP adresi alınamadı:', error);
      }
    };
    getIP();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Sipariş verilerini hazırla
      const orderData = {
        userId: currentUser?.uid,
        userEmail: currentUser?.email,
        shippingAddress: {
          fullName: formData.fullName || '',
          phone: formData.phone || '',
          address: formData.address || '',
          city: formData.city || '',
          district: formData.district || '',
          zipCode: formData.zipCode || ''
        },
        items: cart.map(item => ({
          id: item.id || '',
          title: item.title || '',
          price: item.price || 0,
          quantity: item.quantity || 1,
          selectedSize: item.selectedSize || '',
          selectedColor: item.selectedColor || '',
          image: item.images?.[0] || ''
        })),
        total: calculateTotal() || 0,
        status: 'Bekliyor',
        paymentMethod: paymentMethod || 'creditCard',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Siparişi oluştur
      const orderRef = collection(db, 'orders');
      const docRef = await addDoc(orderRef, orderData);

      // Kredi kartı seçildiğinde
      if (paymentMethod === 'creditCard') {
        console.log('Kredi Kartı Ödeme Bilgileri:', {
          amount: calculateTotal(),
          orderId: `ORDER${Date.now()}`,
          customerEmail: currentUser?.email,
          customerIP: customerIP,
          installmentCount: "0",
          successUrl: 'https://kaylabutik.shop/payment/success',
          errorUrl: 'https://kaylabutik.shop/payment/error'
        });
        setShowCreditCardForm(true);
        return (
          <CreditCardPayment 
            amount={calculateTotal()}
            orderId={docRef.id}
            customerEmail={currentUser?.email || ''}
            customerIP={customerIP}
            installmentCount="0"
            onPaymentComplete={(result) => {
              if (result.success) {
                clearCart();
                setOrderComplete(true);
              } else {
                setError(result.error);
              }
            }}
          />
        );
      }

      // Havale seçeneği için
      clearCart();
      setOrderComplete(true);
      
    } catch (error) {
      console.error('Sipariş oluşturma hatası:', error);
      setError('Sipariş oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOrderComplete(false);
    navigate('/my-orders');
  };

  if (!currentUser) {
    return null;
  }

  return (
    <>
      <Box sx={{ mt: { xs: '56px', sm: '64px' }, pt: 4 }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h1" gutterBottom>
            Sipariş Oluştur
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Sepet Özeti */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Sepet Özeti
            </Typography>
            {cart.map((item) => (
              <Box key={item.id} sx={{ mb: 1 }}>
                <Typography>
                  {item.title} x {item.quantity} = {(item.price * item.quantity).toLocaleString('tr-TR')} TL
                </Typography>
              </Box>
            ))}
            <Typography variant="h6" sx={{ mt: 2 }}>
              Toplam: {calculateTotal().toLocaleString('tr-TR')} TL
            </Typography>
          </Box>

          {showCreditCardForm ? (
            <CreditCardPayment 
              amount={calculateTotal()}
              orderId={`ORDER${Date.now()}`}
              customerEmail={currentUser?.email || ''}
              customerIP={customerIP}
              installmentCount="0"
              onPaymentComplete={(result) => {
                if (result.success) {
                  clearCart();
                  setOrderComplete(true);
                } else {
                  setError(result.error);
                }
              }}
            />
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    name="fullName"
                    label="Ad Soyad"
                    value={formData.fullName}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="address"
                    label="Adres"
                    value={formData.address}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="city"
                    label="Şehir"
                    value={formData.city}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="phone"
                    label="Telefon"
                    value={formData.phone}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="district"
                    label="İlçe"
                    value={formData.district}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="zipCode"
                    label="Posta Kodu"
                    value={formData.zipCode}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>

              {/* Ödeme Yöntemi */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Ödeme Yöntemi
                </Typography>
                <RadioGroup
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <FormControlLabel
                    value="creditCard"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1">Kredi Kartı</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Güvenli ödeme altyapısı ile ödeyin
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="havale"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1">Havale / EFT</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Siparişinizi verdikten sonra banka bilgileri mail olarak gönderilecektir
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </Box>

              <Box sx={{ mt: 4 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || cart.length === 0}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Siparişi Tamamla'
                  )}
                </Button>
              </Box>
            </form>
          )}
        </Container>
      </Box>

      <Dialog
        open={orderComplete}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleOutline 
            sx={{ 
              fontSize: 80, 
              color: 'success.main',
              mb: 2
            }} 
          />
          <DialogTitle>
            Siparişiniz Başarıyla Oluşturuldu!
          </DialogTitle>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Siparişiniz başarıyla alındı. Siparişinizin durumunu "Siparişlerim" 
            sayfasından takip edebilirsiniz.
          </Typography>
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            fullWidth
          >
            Siparişlerimi Görüntüle
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Checkout; 